import { ColumnDef } from "@tanstack/react-table";
import { Unit } from "@/app/types/unit";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2 } from "lucide-react";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Define filterable columns for the data table
export const filterableColumns = [
  {
    id: "is_base",
    title: "Base Unit",
    options: [
      { label: "Base", value: "true" },
      { label: "Not Base", value: "false" },
    ],
  },
  {
    id: "is_active",
    title: "Status",
    options: [
      { label: "Active", value: "true" },
      { label: "Inactive", value: "false" },
    ],
  },
  {
    id: "organization_id",
    title: "Type",
    options: [
      { label: "Global", value: "null" },
      { label: "Organization", value: "not_null" },
    ],
  },
];

export const columns: ColumnDef<Unit>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
  },
  {
    accessorKey: "code",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Code" />,
    cell: ({ row }) => <span className="font-mono">{row.original.code}</span>,
  },
  {
    accessorKey: "description",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
    cell: ({ row }) => row.original.description || "-",
  },
  {
    accessorKey: "is_base",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Base Unit" />,
    cell: ({ row }): JSX.Element => (
      row.original.is_base ? (
        <Badge variant="default">Base</Badge>
      ) : (
        <Badge variant="outline">No</Badge>
      )
    ),
    filterFn: (row, id, value: string[]): boolean => {
      if (!value.length) return true;
      const isBase = row.original.is_base;
      return value.includes(isBase ? "true" : "false");
    },
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => (
      row.original.is_active ? (
        <Badge className="bg-success">Active</Badge>
      ) : (
        <Badge variant="destructive">Inactive</Badge>
      )
    ),
    filterFn: (row, id, value: string[]): boolean => {
      if (!value.length) return true;
      const isActive = row.original.is_active;
      return value.includes(isActive ? "true" : "false");
    },
  },
  {
    accessorKey: "organization_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }): JSX.Element => (
      row.original.organization_id ? (
        <Badge variant="secondary">Organization</Badge>
      ) : (
        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">Global</Badge>
      )
    ),
    filterFn: (row, id, value: string[]): boolean => {
      if (!value.length) return true;
      const orgId = row.original.organization_id;
      if (value.includes("null") && orgId === null) return true;
      if (value.includes("not_null") && orgId !== null) return true;
      return false;
    },
  },
  {
    id: "actions",
    cell: ({ row }): JSX.Element => {
      const unit = row.original;
      const isGlobal = !unit.organization_id;
      
      // Custom event dispatcher
      const dispatchEvent = (eventName: string, detail: Unit): void => {
        const event = new CustomEvent(eventName, { detail });
        document.dispatchEvent(event);
      };
      
      return (
        <div className="flex items-center justify-end gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => dispatchEvent("edit-unit", unit)}
                  // Only org admins can edit org units, and nobody can edit global units
                  disabled={isGlobal}
                >
                  <Edit className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isGlobal ? "Cannot edit global units" : "Edit unit"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => dispatchEvent("delete-unit", unit)}
                  // Only org admins can delete org units, and nobody can delete global units
                  disabled={isGlobal}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isGlobal ? "Cannot delete global units" : "Delete unit"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
]; 